import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { TenantWorkspaceApi, type ServiceArea, type AssignServiceAreaRequest, type FulfillmentMode, type Serviceability, type CheckServiceabilityRequest, type VendorLocation, type CoverageType } from '@/modules/tenants/services/workspace-api';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { ErrorState } from '@/shared/feedback';
import { FiPlus, FiMapPin, FiShield, FiRefreshCw, FiAlertCircle, FiSearch, FiTruck, FiHome, FiTarget } from 'react-icons/fi';

const FULFILLMENT_MODES: { value: FulfillmentMode; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'at_customer', label: 'At Customer', icon: FiTruck },
  { value: 'delivery', label: 'Delivery', icon: FiHome },
];

const COVERAGE_TYPES: { value: CoverageType; label: string }[] = [
  { value: 'postal', label: 'Postal code' },
  { value: 'radius', label: 'Radius around the location' },
];

/** The API refuses a radius coverage above this. */
const MAX_RADIUS_METERS = 100_000;

export function ServiceAreasPage(): ReactNode {
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [locations, setLocations] = useState<VendorLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const errorMessage = error?.message ?? 'An unknown error occurred';
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [formData, setFormData] = useState<AssignServiceAreaRequest>({
    locationId: '',
    coverageType: 'postal',
    postalCode: '',
    radiusMeters: 3000,
    modes: ['delivery'],
  });
  const [checkResult, setCheckResult] = useState<Serviceability | null>(null);
  const [checkInput, setCheckInput] = useState<CheckServiceabilityRequest>({
    mode: 'delivery',
    postalCode: '',
  });

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const profiles = await TenantWorkspaceApi.listVendorProfiles();
      const own = profiles[0];
      const areas = await TenantWorkspaceApi.listServiceAreas();
      const locs = own ? await TenantWorkspaceApi.listLocations(own.id) : [];
      setServiceAreas(areas);
      setLocations(locs);
      if (!formData.locationId && locs.length > 0 && locs[0]?.id) {
        setFormData(prev => ({ ...prev, locationId: locs[0]!.id }));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load service areas'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAssign = async () => {
    if (!formData.locationId || formData.modes.length === 0) {
      setError(new Error('A location and at least one fulfilment mode are required'));
      return;
    }
    if (formData.coverageType === 'postal' && !formData.postalCode?.trim()) {
      setError(new Error('A postal coverage needs a postal code'));
      return;
    }
    if (formData.coverageType === 'radius' && !formData.radiusMeters) {
      setError(new Error('A radius coverage needs a radius'));
      return;
    }
    setIsAssigning(true);
    setError(null);
    try {
      await TenantWorkspaceApi.assignServiceArea({
        ...formData,
        postalCode:
          formData.coverageType === 'postal'
            ? formData.postalCode?.trim().toUpperCase() ?? null
            : null,
        radiusMeters: formData.coverageType === 'radius' ? formData.radiusMeters : null,
      });
      setShowAssignForm(false);
      setFormData(prev => ({ ...prev, postalCode: '', modes: ['delivery'] }));
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to assign service area'));
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCheckServiceability = async () => {
    if (!checkInput.postalCode.trim()) {
      setError(new Error('Postal code is required'));
      return;
    }
    setIsChecking(true);
    setError(null);
    try {
      const result = await TenantWorkspaceApi.checkServiceability({
        ...checkInput,
        postalCode: checkInput.postalCode.trim().toUpperCase(),
      });
      setCheckResult(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to check serviceability'));
    } finally {
      setIsChecking(false);
    }
  };

  const handleUpdateModes = async (areaId: string, modes: FulfillmentMode[]) => {
    setError(null);
    try {
      await TenantWorkspaceApi.updateServiceArea(areaId, { modes });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update modes'));
    }
  };

  const handleSetStatus = async (areaId: string, status: 'active' | 'disabled') => {
    setError(null);
    try {
      await TenantWorkspaceApi.updateServiceArea(areaId, { status });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update status'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return <ErrorState title="Could not load service areas" message={errorMessage} onRetry={loadData} />;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FiShield className="h-5 w-5" />
            Service Areas
          </h2>
          <p className="text-sm text-content-muted mt-1">
            Configure postal code coverage for at-customer service and delivery. No coverage = not serviceable anywhere.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={loadData} disabled={isLoading}>
            <FiRefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowAssignForm(true)}>
            <FiPlus className="h-4 w-4 mr-2" />
            Add Coverage
          </Button>
        </div>
      </div>

      {error && <ErrorState title="Operation failed" message={errorMessage} />}

      {/* Serviceability Checker */}
      <section className="rounded-lg border border-border p-6 space-y-4 bg-muted/30">
        <h3 className="text-lg font-medium">Check Serviceability</h3>
        <p className="text-sm text-content-muted">Test if a postal code is serviceable for this tenant.</p>

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            className="rounded-md border border-border bg-background px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-48"
            value={checkInput.mode}
            onChange={(e) => setCheckInput(prev => ({ ...prev, mode: e.currentTarget.value as FulfillmentMode }))}
          >
            {FULFILLMENT_MODES.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <Input
            label="Postal Code"
            value={checkInput.postalCode}
            onChange={(e) => setCheckInput(prev => ({ ...prev, postalCode: e.currentTarget.value.toUpperCase() }))}
            placeholder="e.g., 560001"
            className="flex-1 sm:w-64"
          />
          {locations.length > 0 && (
            <select
              className="rounded-md border border-border bg-background px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-64"
              value={checkInput.locationId || ''}
              onChange={(e) => setCheckInput(prev => ({ ...prev, locationId: e.currentTarget.value || undefined }))}
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.label}</option>
              ))}
            </select>
          )}
          <Button onClick={handleCheckServiceability} loading={isChecking} disabled={isChecking}>
            <FiSearch className="h-4 w-4 mr-2" />
            Check
          </Button>
        </div>

        {checkResult && (
          <div className={`rounded-lg p-4 flex items-center gap-3 ${
            checkResult.result === 'serviceable' ? 'bg-green-50 border-green-200' :
            checkResult.result === 'not_serviceable' ? 'bg-red-50 border-red-200' :
            'bg-yellow-50 border-yellow-200'
          }`}>
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              checkResult.result === 'serviceable' ? 'bg-green-100 text-green-600' :
              checkResult.result === 'not_serviceable' ? 'bg-red-100 text-red-600' :
              'bg-yellow-100 text-yellow-600'
            }`}>
              {checkResult.result === 'serviceable' && <FiShield className="h-5 w-5" />}
              {checkResult.result === 'not_serviceable' && <FiAlertCircle className="h-5 w-5" />}
              {checkResult.result === 'insufficient_geography' && <FiAlertCircle className="h-5 w-5" />}
            </div>
            <div>
              <p className="font-medium text-content">
                {checkResult.result === 'serviceable' && 'Serviceable'}
                {checkResult.result === 'not_serviceable' && 'Not Serviceable'}
                {checkResult.result === 'insufficient_geography' && 'Insufficient Geography'}
              </p>
              <p className="text-sm text-content-muted">
                {checkResult.matchedBy ? `Matched by: ${checkResult.matchedBy}` : 'No active coverage found'}
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Assign Coverage Form */}
      {showAssignForm && (
        <section className="rounded-lg border border-border p-6 space-y-4 bg-muted/30">
          <h3 className="text-lg font-medium">Add Coverage</h3>

          <select
            className="rounded-md border border-border bg-background px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-64"
            value={formData.locationId}
            onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.currentTarget.value }))}
            required
          >
            <option value="">Select Location *</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.label}</option>
            ))}
          </select>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-content" htmlFor="coverage-type">
                Coverage type
              </label>
              <select
                id="coverage-type"
                className="rounded-md border border-border bg-background px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={formData.coverageType}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, coverageType: e.currentTarget.value as CoverageType }))
                }
              >
                {COVERAGE_TYPES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {formData.coverageType === 'postal' ? (
              <Input
                label="Postal Code *"
                value={formData.postalCode ?? ''}
                onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.currentTarget.value.toUpperCase() }))}
                placeholder="e.g., 560001"
              />
            ) : (
              <Input
                label="Radius (metres) *"
                value={String(formData.radiusMeters ?? '')}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, radiusMeters: Number(e.currentTarget.value) }))
                }
                inputMode="numeric"
                hint={`Up to ${MAX_RADIUS_METERS} metres. The location must have coordinates.`}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-content mb-2">Fulfillment Modes *</label>
            <div className="flex flex-wrap gap-3">
              {FULFILLMENT_MODES.map(mode => (
                <label key={mode.value} className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.modes.includes(mode.value)}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      modes: e.currentTarget.checked
                        ? [...prev.modes, mode.value]
                        : prev.modes.filter(m => m !== mode.value),
                    }))}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <mode.icon className="h-4 w-4" />
                  <span className="text-sm text-content">{mode.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t border-border">
            <Button variant="secondary" onClick={() => setShowAssignForm(false)}>Cancel</Button>
            <Button onClick={handleAssign} loading={isAssigning} disabled={isAssigning}>
              <FiPlus className="h-4 w-4 mr-2" />
              Add Coverage
            </Button>
          </div>
        </section>
      )}

      {/* Service Areas List */}
      <section className="rounded-lg border border-border overflow-hidden">
        {serviceAreas.length === 0 ? (
          <div className="p-12 text-center text-content-muted">
            <FiShield className="h-12 w-12 mx-auto mb-4 text-content-muted/50" />
            <p className="text-lg">No service areas configured</p>
            <p className="text-sm mt-1">Add postal code coverage to enable serviceability</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {serviceAreas.map((area) => (
              <ServiceAreaRow
                key={area.id}
                area={area}
                locations={locations}
                onUpdateModes={handleUpdateModes}
                onSetStatus={handleSetStatus}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface ServiceAreaRowProps {
  area: ServiceArea;
  locations: VendorLocation[];
  onUpdateModes: (id: string, modes: FulfillmentMode[]) => void;
  onSetStatus: (id: string, status: 'active' | 'disabled') => void;
}

function ServiceAreaRow({ area, locations, onUpdateModes, onSetStatus }: ServiceAreaRowProps) {
  const location = locations.find(l => l.id === area.vendorLocationId);
  const modeLabels = area.fulfillmentModes.map(m => FULFILLMENT_MODES.find(f => f.value === m)?.label || m).join(', ');
  const coverageLabel =
    area.coverageType === 'postal'
      ? area.postalCode ?? '—'
      : `${area.radiusMeters ?? '—'} m radius`;

  return (
    <div className="p-4 hover:bg-muted/30 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted text-content-muted">
            {area.coverageType === 'radius' ? (
              <FiTarget className="h-5 w-5" />
            ) : (
              <FiMapPin className="h-5 w-5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-content truncate">{coverageLabel}</h4>
              <Badge tone={area.status === 'active' ? 'positive' : 'neutral'}>
                {area.status === 'active' ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <p className="text-sm text-content-muted">{location?.label || area.vendorLocationId.slice(0, 8)}…</p>
            <p className="text-sm text-content-muted">{area.country} • {modeLabels}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            {FULFILLMENT_MODES.map(mode => (
              <label key={mode.value} className="flex items-center gap-1 cursor-pointer px-2 py-1 rounded border text-xs">
                <input
                  type="checkbox"
                  checked={area.fulfillmentModes.includes(mode.value)}
                  onChange={(e) => {
                    const newModes = e.currentTarget.checked
                      ? [...area.fulfillmentModes, mode.value]
                      : area.fulfillmentModes.filter(m => m !== mode.value);
                    onUpdateModes(area.id, newModes);
                  }}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <mode.icon className="h-3 w-3" />
              </label>
            ))}
          </div>

          <Button
            variant={area.status === 'active' ? 'secondary' : 'primary'}
            size="sm"
            onClick={() => onSetStatus(area.id, area.status === 'active' ? 'disabled' : 'active')}
            className="text-xs px-2"
          >
            {area.status === 'active' ? 'Disable' : 'Enable'}
          </Button>
        </div>
      </div>
    </div>
  );
}